/**
 * Step 3: MoveIt2 Configuration
 * Planning groups, controllers, and collision matrix
 */

import { useState } from "react";
import {
  Box,
  Title,
  Text,
  Tabs,
  Stack,
  Paper,
  Button,
  TextInput,
  Select,
  MultiSelect,
  Group,
  ActionIcon,
  Table,
  Badge,
} from "@mantine/core";
import { IconPlus, IconTrash, IconGripVertical } from "@tabler/icons-react";
import { useConfigureStore, ControllerConfig } from "../ConfigureStore";

function PlanningGroupEditor() {
  const {
    joints,
    links,
    planningGroups,
    addPlanningGroup,
    removePlanningGroup,
  } = useConfigureStore();

  const [newGroupName, setNewGroupName] = useState("");
  const [selectedJoints, setSelectedJoints] = useState<string[]>([]);
  const [endEffector, setEndEffector] = useState<string | null>(null);

  const jointOptions = joints
    .filter((j) => j.type !== "fixed")
    .map((j) => ({ value: j.name, label: j.name }));

  const linkOptions = links.map((l) => ({ value: l.name, label: l.name }));

  const handleAddGroup = () => {
    if (newGroupName.trim() && selectedJoints.length > 0) {
      addPlanningGroup({
        name: newGroupName.trim(),
        joints: selectedJoints,
        endEffectorLink: endEffector,
        baseLink: null,
      });
      setNewGroupName("");
      setSelectedJoints([]);
      setEndEffector(null);
    }
  };

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Add Planning Group
        </Title>
        <Stack gap="sm">
          <TextInput
            label="Group Name"
            placeholder="e.g., arm, gripper"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.currentTarget.value)}
          />
          <MultiSelect
            label="Joints"
            placeholder="Select joints for this group"
            data={jointOptions}
            value={selectedJoints}
            onChange={setSelectedJoints}
            searchable
          />
          <Select
            label="End Effector Link (optional)"
            placeholder="Select end effector link"
            data={linkOptions}
            value={endEffector}
            onChange={setEndEffector}
            clearable
            searchable
          />
          <Button
            onClick={handleAddGroup}
            disabled={!newGroupName.trim() || selectedJoints.length === 0}
          >
            Add Group
          </Button>
        </Stack>
      </Paper>

      {planningGroups.length > 0 && (
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Planning Groups ({planningGroups.length})
          </Title>
          <Stack gap="sm">
            {planningGroups.map((group, index) => (
              <Paper key={index} withBorder p="sm" bg="gray.0">
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconGripVertical size={14} style={{ color: "var(--mantine-color-gray-5)" }} />
                      <Text fw={500}>{group.name}</Text>
                    </Group>
                    <Group gap="xs">
                      {group.joints.map((joint) => (
                        <Badge key={joint} size="xs" variant="light">
                          {joint}
                        </Badge>
                      ))}
                    </Group>
                    {group.endEffectorLink && (
                      <Text size="xs" c="dimmed">
                        End Effector: {group.endEffectorLink}
                      </Text>
                    )}
                  </Stack>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => removePlanningGroup(index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function ControllerEditor() {
  const {
    joints,
    controllers,
    addController,
    removeController,
  } = useConfigureStore();

  const [newControllerName, setNewControllerName] = useState("");
  const [controllerType, setControllerType] = useState<string | null>(
    "joint_trajectory_controller"
  );
  const [selectedJoints, setSelectedJoints] = useState<string[]>([]);

  const jointOptions = joints
    .filter((j) => j.type !== "fixed")
    .map((j) => ({ value: j.name, label: j.name }));

  const controllerTypeOptions = [
    { value: "joint_trajectory_controller", label: "Joint Trajectory Controller" },
    { value: "gripper_controller", label: "Gripper Controller" },
    { value: "position_controller", label: "Position Controller" },
  ];

  const handleAddController = () => {
    if (newControllerName.trim() && controllerType && selectedJoints.length > 0) {
      addController({
        name: newControllerName.trim(),
        type: controllerType as ControllerConfig["type"],
        joints: selectedJoints,
      });
      setNewControllerName("");
      setSelectedJoints([]);
    }
  };

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Add Controller
        </Title>
        <Stack gap="sm">
          <TextInput
            label="Controller Name"
            placeholder="e.g., arm_controller"
            value={newControllerName}
            onChange={(e) => setNewControllerName(e.currentTarget.value)}
          />
          <Select
            label="Controller Type"
            data={controllerTypeOptions}
            value={controllerType}
            onChange={setControllerType}
          />
          <MultiSelect
            label="Joints"
            placeholder="Select joints for this controller"
            data={jointOptions}
            value={selectedJoints}
            onChange={setSelectedJoints}
            searchable
          />
          <Button
            onClick={handleAddController}
            disabled={!newControllerName.trim() || !controllerType || selectedJoints.length === 0}
          >
            Add Controller
          </Button>
        </Stack>
      </Paper>

      {controllers.length > 0 && (
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Controllers ({controllers.length})
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Joints</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {controllers.map((controller, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {controller.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">
                      {controller.type}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {controller.joints.slice(0, 3).map((joint) => (
                        <Badge key={joint} size="xs" variant="outline">
                          {joint}
                        </Badge>
                      ))}
                      {controller.joints.length > 3 && (
                        <Text size="xs" c="dimmed">
                          +{controller.joints.length - 3} more
                        </Text>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeController(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function CollisionMatrixEditor() {
  const {
    links,
    disabledCollisionPairs,
    addDisabledCollisionPair,
    removeDisabledCollisionPair,
  } = useConfigureStore();

  const [link1, setLink1] = useState<string | null>(null);
  const [link2, setLink2] = useState<string | null>(null);

  const linkOptions = links.map((l) => ({ value: l.name, label: l.name }));

  const handleAddPair = () => {
    if (link1 && link2 && link1 !== link2) {
      addDisabledCollisionPair(link1, link2);
      setLink1(null);
      setLink2(null);
    }
  };

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Disable Collision Pair
        </Title>
        <Group align="flex-end">
          <Select
            label="Link 1"
            placeholder="Select first link"
            data={linkOptions}
            value={link1}
            onChange={setLink1}
            searchable
            style={{ flex: 1 }}
          />
          <Select
            label="Link 2"
            placeholder="Select second link"
            data={linkOptions}
            value={link2}
            onChange={setLink2}
            searchable
            style={{ flex: 1 }}
          />
          <Button onClick={handleAddPair} disabled={!link1 || !link2 || link1 === link2}>
            <IconPlus size={16} />
          </Button>
        </Group>
      </Paper>

      {disabledCollisionPairs.length > 0 && (
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Disabled Collision Pairs ({disabledCollisionPairs.length})
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Link 1</Table.Th>
                <Table.Th>Link 2</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {disabledCollisionPairs.map((pair, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <Badge variant="light">{pair[0]}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{pair[1]}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeDisabledCollisionPair(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

export function MoveItStep() {
  const [activeTab, setActiveTab] = useState<string | null>("groups");

  return (
    <Stack gap="lg">
      <Box>
        <Title order={3} mb="xs">
          MoveIt2 Configuration
        </Title>
        <Text c="dimmed" size="sm">
          Configure planning groups, controllers, and collision settings for MoveIt2.
        </Text>
      </Box>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="groups">Planning Groups</Tabs.Tab>
          <Tabs.Tab value="controllers">Controllers</Tabs.Tab>
          <Tabs.Tab value="collisions">Collision Matrix</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="groups" pt="md">
          <PlanningGroupEditor />
        </Tabs.Panel>

        <Tabs.Panel value="controllers" pt="md">
          <ControllerEditor />
        </Tabs.Panel>

        <Tabs.Panel value="collisions" pt="md">
          <CollisionMatrixEditor />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
